import React from "react";
import { Redirect } from "wouter";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2 } from "lucide-react";

const loginSchema = z.object({
  username: z.string().min(3, "Username must be at least 3 characters"),
  password: z.string().min(6, "Password must be at least 6 characters"),
});

type LoginFormValues = z.infer<typeof loginSchema>;

const AuthPage: React.FC = () => {
  const { t } = useTranslation();
  const { user, loginMutation } = useAuth();
  const { toast } = useToast();
  const [location] = React.useState<string>(
    // Get the "from" URL parameter or default to "/admin/feedback"
    new URLSearchParams(window.location.search).get("from") || "/admin/feedback"
  );

  const loginForm = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      username: "",
      password: "",
    },
  });

  const onLoginSubmit = (values: LoginFormValues) => {
    loginMutation.mutate(values, {
      onSuccess: () => {
        // This callback will be called after the login mutation succeeds
        window.location.href = location;
      }
    });
  };

  // Redirect to the requested page if user is already logged in
  if (user) {
    return <Redirect to={location} />;
  }

  return (
    <div className="flex min-h-screen">
      {/* Form column */}
      <div className="flex w-full lg:w-1/2 items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <div className="flex justify-center mb-8">
            <img 
              src="/assets/ui/LOADER-MJALLAL.webp"
              alt="MJ Logo"
              className="w-32 h-32"
            />
          </div>
          
          <Card>
            <CardHeader className="text-center">
              <CardTitle>{t("Login to Dashboard")}</CardTitle>
              <CardDescription>{t("Enter your credentials to access the admin dashboard")}</CardDescription>
            </CardHeader>
            <CardContent>
              <Form {...loginForm}>
                <form onSubmit={loginForm.handleSubmit(onLoginSubmit)} className="space-y-6">
                  <FormField
                    control={loginForm.control}
                    name="username"
                    render={({ field }: { field: any }) => (
                      <FormItem>
                        <FormLabel>{t("Username")}</FormLabel>
                        <FormControl>
                          <Input placeholder={t("Enter username")} {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={loginForm.control}
                    name="password"
                    render={({ field }: { field: any }) => (
                      <FormItem>
                        <FormLabel>{t("Password")}</FormLabel>
                        <FormControl>
                          <Input type="password" placeholder={t("Enter password")} {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <Button type="submit" className="w-full" disabled={loginMutation.isPending}>
                    {loginMutation.isPending ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {t("Logging in...")}
                      </>
                    ) : (
                      t("Login")
                    )}
                  </Button>
                </form>
              </Form>
            </CardContent>
          </Card>
        </div>
      </div>
      
      {/* Hero column - only visible on larger screens */}
      <div className="hidden lg:flex w-1/2 bg-primary/10 flex-col items-center justify-center px-12 py-12">
        <div className="max-w-md text-center">
          <h1 className="text-4xl font-bold mb-6 text-primary">{t("Leen Feedback Dashboard")}</h1>
          <p className="text-lg mb-8 text-foreground/80">
            {t("Access the admin dashboard to view user feedback and CSAT ratings. Monitor user satisfaction and address issues in real-time.")}
          </p>
          <div className="space-y-4">
            <div className="flex items-center p-4 bg-background rounded-lg shadow-sm">
              <div className="mr-4 bg-primary/20 p-3 rounded-full">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold">{t("Real-time Feedback")}</h3>
                <p className="text-sm text-muted-foreground">{t("Monitor user satisfaction in real-time")}</p>
              </div>
            </div>
            <div className="flex items-center p-4 bg-background rounded-lg shadow-sm">
              <div className="mr-4 bg-primary/20 p-3 rounded-full">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold">{t("Comprehensive Analytics")}</h3>
                <p className="text-sm text-muted-foreground">{t("Track trends and patterns in user feedback")}</p>
              </div>
            </div>
            <div className="flex items-center p-4 bg-background rounded-lg shadow-sm">
              <div className="mr-4 bg-primary/20 p-3 rounded-full">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold">{t("Secure Access")}</h3>
                <p className="text-sm text-muted-foreground">{t("Protected dashboard with admin-only access")}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;